// ═══════════════════════════════════════════════════════════════
// v3.3.683 — التحسينات (Improvement Tickets)
// كل مستخدم يرفع تذكرة تحسين (عنوان + توضيح + الصفحة/القسم + صور/فيديو)
// ويرى تذاكره فقط؛ الأدمن يرى الكل ويعالجها (الحالة + رد).
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from "react";
import { tickets as ticketsApi } from "../services/api";
import toast from "react-hot-toast";
import { FiPlus, FiTrash2, FiPaperclip, FiTool } from "react-icons/fi";

// v3.3.687 — كل قسم مربوط بصلاحيات العرض الخاصة به: المستخدم يرى فقط أقسام الموديولات المتاحة له
const SECTIONS = [
  { label: "الحجوزات",                  perms: ["can_view_reservations"] },
  { label: "المشاريع / الوحدات",        perms: ["can_view_projects"] },
  { label: "العملاء / المسوقين",        perms: ["can_view_clients", "can_view_developers", "can_view_marketers"] },
  { label: "المدفوعات",                 perms: ["can_view_payments"] },
  { label: "التحصيلات والديون",         perms: ["can_view_collections"] },
  { label: "الإحصائيات / التحليل المالي", perms: ["can_view_analytics", "can_view_financial_reports"] },
  { label: "التدفقات النقدية",          perms: ["can_view_cash_flow", "can_view_cash_flow_statement"] },
  { label: "الخزينة المالية",           perms: ["can_view_treasury"] },
  { label: "البرنامج المحاسبي",         perms: ["can_view_accounting"] },
  { label: "التمويل (الصكوك)",          perms: ["can_view_sukuk"] },
  { label: "المصاريف الرأسمالية",       perms: ["can_view_capex"] },
  { label: "التسويق / الواتساب",        perms: ["can_view_client_comm", "can_view_marketer_comm", "can_view_wa_abubader2", "can_view_wa_abubader_biz", "can_view_business_comm"] },
  { label: "الإعدادات / الصلاحيات",     perms: ["can_view_settings"] },
  { label: "أخرى",                      perms: [] }, // متاح للجميع
];
const visibleSections = (user, isAdmin) =>
  SECTIONS.filter(s => isAdmin || s.perms.length === 0 || s.perms.some(p => user?.[p]));
const STATUS_COLORS = {
  "جديد":         { bg: "#dbeafe", color: "#1e40af" },
  "قيد المعالجة": { bg: "#fef3c7", color: "#92400e" },
  "تم":           { bg: "#dcfce7", color: "#166534" },
  "مرفوض":        { bg: "#fee2e2", color: "#991b1b" },
};
const STATUSES = Object.keys(STATUS_COLORS);

const inp = { width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" };
const lbl = { fontSize: 10, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 4 };

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: "#f1f5f9", color: "#475569" };
  return <span style={{ background: c.bg, color: c.color, borderRadius: 12, padding: "2px 12px", fontSize: 11, fontWeight: 700 }}>{status}</span>;
}

// ─── مودال رفع تذكرة جديد ───
function NewTicketModal({ onClose, onSaved, user, isAdmin }) {
  const sections = visibleSections(user, isAdmin);
  const [title, setTitle] = useState("");
  const [section, setSection] = useState("");
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("أدخل عنوان التحسين");
    if (!desc.trim()) return toast.error("أدخل توضيح التحسين");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", desc.trim());
      fd.append("page_section", section);
      files.forEach(f => fd.append("files", f));
      const r = await ticketsApi.create(fd);
      const failed = r.data?.failed_uploads || 0;
      toast.success(failed > 0 ? `تم رفع التذكرة (فشل رفع ${failed} مرفق)` : "تم رفع التذكرة بنجاح 🎉");
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || "فشل رفع التذكرة"); }
    setSaving(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div dir="rtl" onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 22, width: 520, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <h3 style={{ margin: "0 0 14px", fontSize: 15 }}>🛠️ رفع تذكرة تحسين</h3>
        <div style={{ marginBottom: 10 }}>
          <label style={lbl}>عنوان التحسين *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} style={{ ...inp, fontWeight: 700 }} autoFocus placeholder="وصف مختصر للتحسين المطلوب" />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={lbl}>الصفحة / القسم في النظام</label>
          <select value={section} onChange={e => setSection(e.target.value)} style={inp}>
            <option value="">— اختر القسم —</option>
            {sections.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={lbl}>توضيح التحسين / المشكلة *</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={4} style={{ ...inp, resize: "vertical" }}
            placeholder="اشرح التحسين المطلوب أو المشكلة بالتفصيل..." />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>📎 مرفقات (صور أو فيديو — حتى 5 ملفات)</label>
          <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: 14, textAlign: "center", background: files.length ? "#f0fdf4" : "#f8fafc", cursor: "pointer" }}
            onClick={() => document.getElementById("ticket-files-input")?.click()}>
            {files.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ fontSize: 11, color: "#166534", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>{f.type.startsWith("video/") ? "🎬" : "🖼️"} {f.name} ({(f.size / (1024 * 1024)).toFixed(1)} MB)</span>
                    <button onClick={e => { e.stopPropagation(); setFiles(files.filter((_, x) => x !== i)); }}
                      style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 11 }}>✕</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "#64748b" }}>اضغط لاختيار صور أو فيديو توضيحي</div>
            )}
            <input id="ticket-files-input" type="file" accept="image/*,video/*" multiple style={{ display: "none" }}
              onChange={e => {
                const picked = Array.from(e.target.files || []);
                setFiles(prev => [...prev, ...picked].slice(0, 5));
                e.target.value = "";
              }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={submit} disabled={saving}
            style={{ flex: 1, padding: 10, background: "#166534", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "wait" : "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>
            {saving ? "⏳ جاري الرفع..." : "🚀 رفع التذكرة"}
          </button>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// ─── بطاقة تذكرة ───
function TicketCard({ t, isAdmin, onChanged }) {
  const [status, setStatus] = useState(t.status);
  const [reply, setReply] = useState(t.admin_reply || "");
  const [saving, setSaving] = useState(false);
  const attachments = Array.isArray(t.attachments) ? t.attachments : [];

  const saveProcess = async () => {
    setSaving(true);
    try {
      await ticketsApi.process(t.id, { status, admin_reply: reply });
      toast.success("تم حفظ المعالجة");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.error || "فشل الحفظ"); }
    setSaving(false);
  };

  const remove = async () => {
    if (!window.confirm(`حذف التذكرة "${t.title}"؟`)) return;
    try { await ticketsApi.remove(t.id); toast.success("تم الحذف"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.error || "فشل الحذف"); }
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>#{t.id} · {t.title}</span>
            <StatusBadge status={t.status} />
            {t.page_section && <span style={{ background: "#eff6ff", color: "#1d4ed8", borderRadius: 12, padding: "2px 10px", fontSize: 10, fontWeight: 600 }}>📍 {t.page_section}</span>}
          </div>
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
            {isAdmin && t.created_by_name ? <>👤 {t.created_by_name} · </> : null}
            {t.created_at ? new Date(t.created_at).toLocaleString("en-GB") : ""}
          </div>
        </div>
        {(t.status === "جديد" || isAdmin) && (
          <button onClick={remove} title="حذف" style={{ background: "none", border: "1px solid #fecaca", borderRadius: 6, padding: "4px 8px", cursor: "pointer", color: "#dc2626" }}><FiTrash2 size={13} /></button>
        )}
      </div>

      {t.description && (
        <div style={{ fontSize: 12, color: "#334155", marginTop: 10, whiteSpace: "pre-wrap", background: "#f8fafc", borderRadius: 8, padding: "8px 12px" }}>{t.description}</div>
      )}

      {attachments.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {attachments.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 6, padding: "3px 10px", fontSize: 11, color: "#2563eb", textDecoration: "none" }}>
              <FiPaperclip size={11} /> {(a.mime || "").startsWith("video/") ? "🎬" : "🖼️"} {a.name}
            </a>
          ))}
        </div>
      )}

      {/* رد الأدمن (عرض للجميع) */}
      {t.admin_reply && !isAdmin && (
        <div style={{ marginTop: 10, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#166534" }}>
          💬 <b>رد الإدارة:</b> {t.admin_reply}
        </div>
      )}

      {/* أدوات المعالجة (أدمن فقط) */}
      {isAdmin && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #e2e8f0", display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
          <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inp, width: 140 }}>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={1} placeholder="رد الإدارة..."
            style={{ ...inp, flex: 1, minWidth: 200, resize: "vertical" }} />
          <button onClick={saveProcess} disabled={saving}
            style={{ padding: "8px 16px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>
            {saving ? "..." : "حفظ المعالجة"}
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════ الصفحة ═══════
export default function TicketsPage({ user, isAdmin }) {
  const [items, setItems] = useState([]);
  const [isAdminView, setIsAdminView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await ticketsApi.list();
      setItems(r.data?.tickets || []);
      setIsAdminView(!!r.data?.is_admin);
    } catch { toast.error("فشل تحميل التذاكر"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter ? items.filter(t => t.status === statusFilter) : items;
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: items.filter(t => t.status === s).length }), {});

  return (
    <div dir="rtl" style={{ padding: 20, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}><FiTool /> التحسينات</h2>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
            {isAdminView ? `كل التذاكر (${items.length}) — أنت تعالجها كأدمن` : `تذاكرك (${items.length}) — ترى تذاكرك فقط`}
          </div>
        </div>
        <button onClick={() => setModal(true)}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", background: "#166534", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          <FiPlus /> رفع تذكرة تحسين
        </button>
      </div>

      {/* فلاتر الحالة */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <button onClick={() => setStatusFilter("")}
          style={{ padding: "6px 14px", borderRadius: 14, fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: !statusFilter ? 700 : 400,
            border: !statusFilter ? "2px solid #2563eb" : "1px solid #e2e8f0", background: !statusFilter ? "#eff6ff" : "#fff", color: !statusFilter ? "#1e40af" : "#64748b" }}>
          الكل ({items.length})
        </button>
        {STATUSES.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            style={{ padding: "6px 14px", borderRadius: 14, fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: statusFilter === s ? 700 : 400,
              border: statusFilter === s ? `2px solid ${STATUS_COLORS[s].color}` : "1px solid #e2e8f0",
              background: statusFilter === s ? STATUS_COLORS[s].bg : "#fff", color: statusFilter === s ? STATUS_COLORS[s].color : "#64748b" }}>
            {s} ({counts[s]})
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", background: "#fff", border: "1px dashed #e2e8f0", borderRadius: 12 }}>
          {statusFilter ? "لا توجد تذاكر بهذه الحالة" : "لا توجد تذاكر بعد — اضغط \"رفع تذكرة تحسين\" للبدء"}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(t => <TicketCard key={t.id} t={t} isAdmin={isAdminView} onChanged={load} />)}
        </div>
      )}

      {modal && <NewTicketModal onClose={() => setModal(false)} onSaved={load} user={user} isAdmin={isAdmin} />}
    </div>
  );
}
